import { Flex, Text, Spacer } from "@hackthenorth/north";
import React from "react";
import styled from "styled-components";

import { GradientText, ErrorPageWrapper } from "src/shared/components";
import { useDeviceSize } from "src/shared/hooks";
import { SomethingWentWrongImg } from "src/static/img";

const Title = styled(GradientText)`
  && {
    width: 100%;
    text-align: center;
    font-size: 35px;
  }

  ${({ theme }) => theme.mediaQueries.largeMobile`
    && {
      font-size: 20px;
    }
  `}
`;

const StyledText = styled(Text)`
  && {
    font-size: 20px;
    white-space: pre-line;
    text-align: center;
  }

  ${({ theme }) => theme.mediaQueries.largeMobile`
    && {
      font-size: 16px;
    }
  `}
`;

export type TAwSnapImage =
  | string
  | React.ComponentType<React.SVGProps<SVGSVGElement>>;

const ImageWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  width: min(360px, 90vw);
  height: min(320px, 38vh);
  margin: 0 auto;

  img,
  svg {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  ${({ theme }) => theme.mediaQueries.medium`
    width: min(300px, 85vw);
    height: min(280px, 32vh);
  `}

  ${({ theme }) => theme.mediaQueries.largeMobile`
    width: min(280px, 90vw);
    height: min(200px, 26vh);
  `}
`;

const StyledErrorPageWrapper = styled(ErrorPageWrapper)`
  width: 100vw;
  height: 100vh;

  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;

  ${({ theme }) => theme.mediaQueries.medium`
    && > div > *:last-child {
      align-items: center !important;
    }
  `}
`;

type TAwSnapProps = {
  title?: string;
  image?: TAwSnapImage;
  error?: Error;
  children?: React.ReactNode;
};

const AwSnap = ({
  title = "Aw, snap!",
  image = SomethingWentWrongImg,
  error,
  children,
}: TAwSnapProps) => {
  const ImageComponent = typeof image === "string" ? null : image;
  const isMediumOrSmaller = useDeviceSize("medium");
  const isLargeMobileOrSmaller = useDeviceSize("largeMobile");

  return (
    <StyledErrorPageWrapper>
      <ImageWrapper>
        {typeof image === "string" ? (
          <img src={image} alt="" aria-hidden />
        ) : (
          ImageComponent && <ImageComponent aria-hidden />
        )}
      </ImageWrapper>
      <Spacer
        height={isMediumOrSmaller ? (isLargeMobileOrSmaller ? 30 : 40) : 40}
      />
      <Flex column align="center">
        <Title gradient="gradientRedBlue172" mods="heading">
          {title}
        </Title>
        <Spacer height={10} />
        <StyledText mods="heading">
          {error?.message ??
            "Something went wrong while displaying this webpage. Try reloading?"}
        </StyledText>
        <Spacer height={isMediumOrSmaller ? 5 : 20} />
        {children}
        <Spacer height={isMediumOrSmaller ? 10 : 50} />
      </Flex>
    </StyledErrorPageWrapper>
  );
};

export default AwSnap;
